import { useOutletContext } from "react-router-dom"
import { useCallback, useState } from "react"
import api from "../services/api"

export const useOutletContextPlanActions = () => {
  const { plan, getPlan, refreshPlans, onDownload } = useOutletContext()
  const [success, setSuccess] = useState(false)
  const [error, setError] = useState(null)
  const [loading, setLoading] = useState(false)

  const handleChooseSelfAsGuide = useCallback(async () => {
    setLoading(true)
    return api
      .chooseSelfAsGuide(plan.token)
      .then(response => {
        setSuccess(true)
        getPlan()
        refreshPlans()
        return response
      })
      .catch(error => {
        setError(true)
        throw error
      })
      .finally(() => {
        setLoading(false)
      })
  }, [plan, getPlan, refreshPlans])

  const handleChooseSomeoneElseAsGuide = useCallback(
    async (firstName, lastName, email) => {
      setLoading(true)
      await api
        .inviteGuide(plan.token, {
          first_name: firstName,
          last_name: lastName,
          email,
        })
        .then(response => {
          setSuccess(true)
          getPlan()
          refreshPlans()
        })
        .catch(error => {
          setError(true)
        })
        .finally(() => {
          setLoading(false)
        })
    },
    [plan, getPlan, refreshPlans],
  )

  const handleResendGuideInvite = useCallback(async (firstName, lastName, email) => {
    setLoading(true)
    setError(null)
    return api
      .resendGuideInvite(plan.token, {
        first_name: firstName,
        last_name: lastName,
        email,
      })
      .then(response => {
        setSuccess(true)
        getPlan()
        refreshPlans()
        return response
      })
      .catch(error => {
        const errorMessage =
          error.response?.data?.message || "Failed to resend guide invite."
        setError({
          message: errorMessage,
          status: error.response?.status,
        })
        throw error
      })
      .finally(() => {
        setLoading(false)
      })
  }, [plan?.token, getPlan, refreshPlans])

  const handleCancelGuideInvite = useCallback(async () => {
    setLoading(true)
    setError(null)
    return api
      .cancelGuideInvite(plan.token)
      .then(response => {
        setSuccess(true)
        getPlan()
        refreshPlans()
        return response
      })
      .catch(error => {
        const errorMessage =
          error.response?.data?.message || "Failed to cancel guide invite."
        setError({
          message: errorMessage,
          status: error.response?.status,
        })
        throw error
      })
      .finally(() => {
        setLoading(false)
      })
  }, [plan?.token, getPlan, refreshPlans])

  const handleInviteStudent = useCallback(
    async ({ firstName, lastName, email }) => {
      setLoading(true)
      setError(null)
      return api
        .inviteStudent(plan.token, {
          first_name: firstName,
          last_name: lastName,
          email,
        })
        .then(response => {
          setSuccess(true)
          getPlan()
          refreshPlans()
          return response
        })
        .catch(error => {
          const errorMessage =
            error.response?.data?.message || "There was an error processing your request."
          const validationErrors = error.response?.data?.errors
          setError({
            message: errorMessage,
            errors: validationErrors,
            status: error.response?.status,
          })
          throw error
        })
        .finally(() => {
          setLoading(false)
        })
    },
    [plan?.token, getPlan, refreshPlans],
  )

  const handleResendStudentInvite = useCallback(() => {
    setLoading(true)
    setError(null)

    const student = plan?.youth ? plan.youth[0] : null

    if (!student) {
      setError({ message: "Student information is missing." })
      setLoading(false)
      return Promise.reject(new Error("Student information is missing."))
    }

    return api
      .inviteStudent(plan.token, {
        first_name: student.first_name,
        last_name: student.last_name,
        email: student.email,
      })
      .then(response => {
        setSuccess(true)
        return response
      })
      .catch(error => {
        const errorMessage =
          error.response?.data?.message || "There was an error processing your request."
        const validationErrors = error.response?.data?.errors
        setError({
          message: errorMessage,
          errors: validationErrors,
          status: error.response?.status,
        })
        throw error
      })
      .finally(() => {
        setLoading(false)
      })
  }, [plan?.token, plan?.youth])

  const handleInviteMentors = useCallback(
    async mentors => {
      setLoading(true)
      setError(null)
      return api
        .inviteMentors(plan.token, { mentors })
        .then(response => {
          setSuccess(true)
          getPlan()
          refreshPlans()
          return response
        })
        .catch(error => {
          const errorMessage =
            error.response?.data?.message || "Failed to invite mentors."
          const validationErrors = error.response?.data?.errors
          setError({
            message: errorMessage,
            errors: validationErrors,
            status: error.response?.status,
          })
          throw error
        })
        .finally(() => {
          setLoading(false)
        })
    },
    [plan?.token, getPlan, refreshPlans],
  )

  const handleEnterResponsesMyself = useCallback(async () => {
    setLoading(true)
    setError(null)
    return api
      .enterResponsesMyself(plan.token)
      .then(response => {
        getPlan()
        refreshPlans()
        return response
      })
      .catch(error => {
        setError({ message: error.response?.data?.message })
        throw error
      })
      .finally(() => setLoading(false))
  }, [plan, getPlan, refreshPlans])

  const handleSendPlan = useCallback(async ({studentEmail, guardians, mentors, guideEmail}) => {
    return await api.completeChecklist(plan.token, {
      studentEmail: studentEmail || "",
      guardians: guardians || [],
      mentors: mentors || [],
      guideEmail: guideEmail || "",
    })
  }, [plan?.token])

  return {
    // Choose a Guide
    handleChooseSelfAsGuide,
    handleChooseSomeoneElseAsGuide,
    handleResendGuideInvite,
    handleCancelGuideInvite,

    // Invite Student
    handleInviteStudent,
    handleResendStudentInvite,
    handleEnterResponsesMyself,

    // Sit With Student (handled automatically by the API)

    // Invite Mentors
    handleInviteMentors,
    // Send the Plan
    handleSendPlan,

    handleDownloadPlan: onDownload,

    success,
    error,
    loading,
  }
}
